import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../lib/api';
import type { AppointmentWithClient, CategorizedAppointments } from '../types/appointment';

async function fetchAllAppointments(
  accessToken: string | null | undefined,
  clientId?: string
) {
  const params = new URLSearchParams();
  if (clientId && clientId !== 'all') {
    params.append('client_id', clientId);
  }
  const queryString = params.toString();
  const url = `/api/appointments${queryString ? `?${queryString}` : ''}`;

  return apiRequest<AppointmentWithClient[]>(url, { method: 'GET' }, accessToken);
}

export function useAllAppointments(clientFilter?: string) {
  const { session } = useAuth();

  const { data: appointmentsData, isLoading, error } = useQuery<AppointmentWithClient[]>({
    queryKey: ['appointments', 'all', clientFilter],
    enabled: Boolean(session),
    queryFn: () => fetchAllAppointments(session?.access_token, clientFilter),
  });

  const appointments = useMemo(() => {
    if (!appointmentsData) {
      return { today: [], upcoming: [], overdue: [], completed: [] };
    }

    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const todayEnd = new Date(todayStart);
    todayEnd.setDate(todayEnd.getDate() + 1);

    const categorized: CategorizedAppointments = {
      today: [],
      upcoming: [],
      overdue: [],
      completed: [],
    };

    appointmentsData.forEach((apt) => {
      const startsAt = new Date(apt.starts_at);

      // Completed tab: all non-scheduled appointments
      if (apt.status !== 'scheduled') {
        categorized.completed.push(apt);
        return;
      }

      // Categorize scheduled appointments by time
      if (startsAt >= todayStart && startsAt < todayEnd) {
        categorized.today.push(apt);
      } else if (startsAt >= todayEnd) {
        categorized.upcoming.push(apt);
      } else {
        categorized.overdue.push(apt);
      }
    });

    // Sort: Today/Upcoming/Overdue earliest first, Completed most recent first
    const sortAsc = (a: AppointmentWithClient, b: AppointmentWithClient) =>
      new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime();

    categorized.today.sort(sortAsc);
    categorized.upcoming.sort(sortAsc);
    categorized.overdue.sort(sortAsc);
    categorized.completed.sort((a, b) =>
      new Date(b.starts_at).getTime() - new Date(a.starts_at).getTime()
    );

    return categorized;
  }, [appointmentsData]);

  return { appointments, isLoading, error };
}
